/**
 * New issue dialog (C anywhere). Suggests likely duplicates as you type the
 * title, plus the labels and assignee similar issues usually get. Anything
 * typed is kept as a draft until it's sent or discarded.
 */
import { CommentEditor, type CommentEditorHandle } from "@/components/editor";
import { ColorDot, StateIconFor } from "@/components/icons";
import { AssigneePicker, Chip, CyclePicker, DatePicker, EstimatePicker, LabelsPicker, MilestonePicker, PriorityPicker, ProjectPicker, StatusPicker, TeamPicker } from "@/components/properties";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { get, post } from "@/lib/api";
import { useMe, useMyTeams, useTeams } from "@/lib/hooks";
import { identifier } from "@/lib/format";
import { create, remove, update } from "@/lib/mutations";
import { keyBetween } from "@/lib/order";
import { getTable, useRows, useTable } from "@/lib/store";
import { createdToast } from "@/lib/toasts";
import type { Id, Issue, IssueDraft, RichText } from "@/lib/types";
import { createIssueStore, readPref, useCreateIssue, writePref } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, FileText, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface Similar {
  matches: { issueId: Id; score: number }[];
  suggestions: { assigneeId?: Id | null; projectId?: Id | null; labelIds?: Id[] };
}

type Draft = Pick<Issue, "teamId" | "stateId" | "priority" | "assigneeId" | "labelIds" | "projectId" | "cycleId" | "estimate" | "dueDate" | "milestoneId" | "parentId">;

const DRAFT_SAVE_AFTER = 1200;

function defaultState(teamId: Id): Id {
  const team = getTable("team")[teamId];
  const states = Object.values(getTable("workflowState")).filter((s) => s.teamId === teamId && !s.archivedAt);
  return (team?.defaultStateId && states.find((s) => s.id === team.defaultStateId)?.id) || states.sort((a, b) => a.position - b.position).find((s) => s.category === "unstarted")?.id || states[0]?.id || "";
}

export function CreateIssueDialog() {
  const { open, defaults } = useCreateIssue();
  const teams = useTeams();
  const myTeams = useMyTeams();
  const navigate = useNavigate();
  const teamTable = useTable("team");
  const issues = useTable("issue");
  const labelTable = useTable("label");
  const ai = !!useMe().data?.features.ai;
  const [title, setTitle] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [more, setMore] = useState(false);
  const [similar, setSimilar] = useState<Similar | null>(null);
  const [aiLabels, setAiLabels] = useState<Id[] | null>(null);
  const [descKey, setDescKey] = useState(0);
  const [initialText, setInitialText] = useState<RichText>(null);
  const [descVersion, setDescVersion] = useState(0);
  const [draftId, setDraftId] = useState<Id | null>(null);
  const editor = useRef<CommentEditorHandle>(null);
  const descRef = useRef<{ json: RichText; text: string }>({ json: null, text: "" });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // "Sent" or "discarded": the close handler then leaves the draft alone.
  const settled = useRef(false);

  useEffect(() => {
    if (!open) return;
    const wanted = defaults.teamId;
    const teamId = (wanted && teamTable[wanted] ? wanted : null) ?? readPref<Id | null>("lastTeam", null) ?? myTeams[0]?.id ?? teams[0]?.id;
    if (!teamId) return;
    settled.current = false;
    setTitle(defaults.title ?? "");
    setDraft({
      teamId,
      stateId: defaults.stateId && getTable("workflowState")[defaults.stateId]?.teamId === teamId ? defaults.stateId : defaultState(teamId),
      priority: defaults.priority ?? 0,
      assigneeId: defaults.assigneeId ?? null,
      labelIds: (defaults.labelIds ?? []).filter((l) => getTable("label")[l]),
      projectId: defaults.projectId ?? null,
      cycleId: defaults.cycleId ?? null,
      estimate: defaults.estimate ?? null,
      dueDate: defaults.dueDate ?? null,
      milestoneId: defaults.milestoneId ?? null,
      parentId: defaults.parentId ?? null,
    });
    setDraftId(defaults.draftId ?? null);
    setSimilar(null);
    setAiLabels(null);
    const seed = defaults.description ?? null;
    setInitialText(seed);
    descRef.current = { json: seed, text: defaults.descriptionText ?? "" };
    setDescVersion(0);
    setDescKey((k) => k + 1);
  }, [open]);

  // Look for duplicates as the title settles.
  useEffect(() => {
    if (!open || !draft || title.trim().length < 5) return setSimilar(null);
    const t = setTimeout(() => {
      get<Similar>(`/api/issues/similar?title=${encodeURIComponent(title.trim())}&teamId=${draft.teamId}`)
        .then(setSimilar)
        .catch(() => setSimilar(null));
    }, 400);
    return () => clearTimeout(t);
  }, [title, draft?.teamId, open]);

  // Ask Claude which labels fit, once there's enough to go on.
  useEffect(() => {
    if (!ai || !open || !draft || title.trim().length < 8) return setAiLabels(null);
    const t = setTimeout(() => {
      post<{ labelIds: Id[] }>("/api/ai/suggest-labels", { teamId: draft.teamId, title: title.trim(), descriptionText: descRef.current.text.slice(0, 4000) })
        .then((r) => setAiLabels(r.labelIds))
        .catch(() => setAiLabels(null));
    }, 900);
    return () => clearTimeout(t);
  }, [ai, title, draft?.teamId, open, descVersion]);

  // ---- drafts -----------------------------------------------------------------------------
  const draftData = (): Partial<IssueDraft> | null => {
    if (!draft) return null;
    const t = title.trim();
    const { json, text } = descRef.current;
    if (!t && !text) return null;
    const { teamId, ...data } = draft;
    return { teamId, title: t, description: json, descriptionText: text, data };
  };
  const saveDraft = () => {
    const d = draftData();
    if (!d) {
      if (draftId) remove("issueDraft", draftId);
      setDraftId(null);
      return null;
    }
    if (draftId) {
      update("issueDraft", draftId, d);
      return draftId;
    }
    const { id } = create("issueDraft", d);
    setDraftId(id);
    return id;
  };
  const scheduleDraftSave = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      if (!settled.current) saveDraft();
    }, DRAFT_SAVE_AFTER);
  };
  useEffect(() => {
    if (!open || !draft) return;
    // Only once something was typed, so merely opening the dialog leaves nothing behind.
    if (title.trim() || descRef.current.text || draftId) scheduleDraftSave();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [title, draft, descVersion, open]);

  const templates = useRows("template", (t) => !draft || !t.teamId || t.teamId === draft.teamId, undefined, [draft?.teamId]);
  const parent = draft?.parentId ? issues[draft.parentId] : undefined;

  if (!draft) return null;
  const set = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const close = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (!settled.current) {
      const id = saveDraft();
      if (id) toast("Draft saved", { description: "Find it under Drafts whenever you're ready.", action: { label: "Open drafts", onClick: () => navigate({ to: "/drafts" }) } });
    }
    createIssueStore.set({ open: false, defaults: {} });
  };

  const discard = () => {
    settled.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (draftId) remove("issueDraft", draftId);
    setDraftId(null);
    createIssueStore.set({ open: false, defaults: {} });
  };

  const submit = () => {
    const t = title.trim();
    if (!t) return toast.error("Give the issue a title.");
    const columnFirst = Object.values(getTable("issue"))
      .filter((i) => i.teamId === draft.teamId && i.stateId === draft.stateId && !i.archivedAt)
      .reduce<string | null>((min, i) => (min === null || i.sortKey < min ? i.sortKey : min), null);
    const { json, text } = descRef.current;
    const data: Record<string, unknown> = { ...draft, title: t, sortKey: keyBetween(null, columnFirst) };
    if (text) {
      data.description = json;
      data.descriptionText = text;
    }
    const { id, done } = create("issue", data as Partial<Issue>);
    writePref("lastTeam", draft.teamId);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (draftId) remove("issueDraft", draftId);
    setDraftId(null);
    done.then(() => {
      const issue = getTable("issue")[id];
      const ident = issue ? identifier(issue, getTable("team")) : "Issue";
      createdToast(`${ident} created`, { open: () => navigate({ to: "/issue/$ident", params: { ident } }), path: `/issue/${ident}` });
    });
    if (more) {
      setTitle("");
      editor.current?.clear();
      descRef.current = { json: null, text: "" };
      setDescVersion(0);
      setSimilar(null);
      setAiLabels(null);
    } else {
      settled.current = true;
      createIssueStore.set({ open: false, defaults: {} });
    }
  };

  const team = teamTable[draft.teamId];
  const suggested = similar?.suggestions;
  const canSuggest = suggested && ((suggested.labelIds?.length ?? 0) > 0 || suggested.assigneeId || suggested.projectId);
  const aiPicks = (aiLabels ?? []).filter((l) => labelTable[l] && !draft.labelIds.includes(l));
  const hasContent = !!title.trim() || !!descRef.current.text || !!draftId;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="top-[12%] max-w-2xl translate-y-0 gap-0 p-0 sm:max-w-2xl max-sm:top-0 max-sm:left-0 max-sm:flex max-sm:h-dvh max-sm:max-w-full max-sm:translate-x-0 max-sm:flex-col max-sm:rounded-none max-sm:pt-[env(safe-area-inset-top)] max-sm:ring-0 max-sm:data-open:slide-in-from-bottom-8" showCloseButton={false} onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && (e.preventDefault(), submit())}>
        <DialogTitle className="sr-only">New issue</DialogTitle>
        <div className="flex items-center gap-2 px-4 pt-3">
          <TeamPicker
            value={draft.teamId}
            onChange={(teamId) => set({ teamId, stateId: defaultState(teamId), cycleId: null, labelIds: draft.labelIds.filter((l) => !getTable("label")[l]?.teamId) })}
          />
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-[13px] text-muted-foreground">{parent ? `Sub-issue of ${identifier(parent, teamTable)}` : draftId ? "Draft" : "New issue"}</span>
          <span className="flex-1" />
          {templates.length > 0 && (
            <select
              aria-label="Use a template"
              className="h-7 rounded-md border border-border/80 bg-background px-2 text-[12.5px] text-muted-foreground"
              value=""
              onChange={(e) => {
                const tpl = templates.find((t) => t.id === e.target.value);
                if (!tpl) return;
                if (tpl.data.title) setTitle(tpl.data.title);
                set({ priority: tpl.data.priority ?? draft.priority, labelIds: tpl.data.labelIds ?? draft.labelIds, estimate: tpl.data.estimate ?? draft.estimate, assigneeId: tpl.data.assigneeId ?? draft.assigneeId, projectId: tpl.data.projectId ?? draft.projectId });
                if (tpl.data.descriptionText) {
                  const doc = { type: "doc" as const, content: tpl.data.descriptionText.split("\n").map((line) => (line ? { type: "paragraph", content: [{ type: "text", text: line }] } : { type: "paragraph" })) };
                  setInitialText(doc);
                  descRef.current = { json: doc, text: tpl.data.descriptionText };
                  setDescVersion((v) => v + 1);
                  setDescKey((k) => k + 1);
                }
              }}
            >
              <option value="">Template…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          <Button variant="ghost" size="icon-sm" onClick={close} aria-label="Close">
            <X />
          </Button>
        </div>
        <div className="px-4 pt-3 max-sm:flex-1 max-sm:overflow-y-auto">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                editor.current?.focus();
              }
            }}
            placeholder="Issue title"
            aria-label="Issue title"
            className="w-full bg-transparent text-[18px] font-semibold tracking-[-0.01em] outline-none placeholder:text-muted-foreground/60 max-sm:text-[20px]"
          />
          <div className="mt-2 max-h-[40vh] min-h-20 overflow-y-auto text-[14px] max-sm:max-h-none max-sm:text-[16px]">
            <CommentEditor
              key={descKey}
              ref={editor}
              placeholder="Add a description… (@ to mention, / for formatting)"
              initial={initialText}
              onSubmit={() => submit()}
              onChange={(json, text) => {
                descRef.current = { json, text };
                setDescVersion((v) => v + 1);
              }}
            />
          </div>
        </div>
        {similar && similar.matches.length > 0 && (
          <div className="mx-4 mt-2 rounded-lg border border-amber-500/30 bg-amber-500/8 p-2 text-[12.5px]">
            <div className="mb-1 font-medium text-amber-700 dark:text-amber-400">Possible duplicates</div>
            {similar.matches.map((m) => {
              const i = issues[m.issueId];
              if (!i) return null;
              const ident = identifier(i, teamTable);
              return (
                <button key={m.issueId} type="button" onClick={() => (close(), navigate({ to: "/issue/$ident", params: { ident } }))} className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-accent">
                  <StateIconFor stateId={i.stateId} />
                  <span className="tabular text-muted-foreground">{ident}</span>
                  <span className="truncate">{i.title}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3 pb-3">
          <StatusPicker teamId={draft.teamId} value={draft.stateId} onChange={(stateId) => set({ stateId })} />
          <PriorityPicker value={draft.priority} onChange={(priority) => set({ priority })} />
          <AssigneePicker value={draft.assigneeId} onChange={(assigneeId) => set({ assigneeId })} />
          <LabelsPicker teamId={draft.teamId} value={draft.labelIds} onChange={(labelIds) => set({ labelIds })} />
          <ProjectPicker teamId={draft.teamId} value={draft.projectId} onChange={(projectId) => set({ projectId, milestoneId: null })} />
          <MilestonePicker projectId={draft.projectId} value={draft.milestoneId} onChange={(milestoneId) => set({ milestoneId })} />
          <CyclePicker teamId={draft.teamId} value={draft.cycleId} onChange={(cycleId) => set({ cycleId })} />
          <EstimatePicker teamId={draft.teamId} value={draft.estimate} onChange={(estimate) => set({ estimate })} />
          <DatePicker value={draft.dueDate} onChange={(dueDate) => set({ dueDate })} />
          {canSuggest && (
            <Chip
              className="border-brand/40 text-brand"
              onClick={() =>
                set({
                  labelIds: [...new Set([...draft.labelIds, ...(suggested!.labelIds ?? [])])].filter((l) => getTable("label")[l]),
                  assigneeId: draft.assigneeId ?? suggested!.assigneeId ?? null,
                  projectId: draft.projectId ?? suggested!.projectId ?? null,
                })
              }
              title="Labels, assignee and project that similar issues have"
            >
              <Sparkles className="size-3.5" /> Use suggestions
            </Chip>
          )}
        </div>
        {aiPicks.length > 0 && (
          <div className="mx-4 mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-brand/25 bg-brand/5 px-2.5 py-1.5 text-[12.5px]">
            <span className="flex items-center gap-1 text-brand">
              <Sparkles className="size-3.5" /> Suggested labels
            </span>
            {aiPicks.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => set({ labelIds: [...draft.labelIds, l] })}
                className={cn("inline-flex h-6 items-center gap-1.5 rounded-full border border-border bg-background px-2 text-[12px] transition-colors hover:border-brand/50 hover:bg-brand/10")}
                title="Add this label"
              >
                <ColorDot color={labelTable[l].color} />
                {labelTable[l].parentId ? `${labelTable[labelTable[l].parentId!]?.name}: ` : ""}
                {labelTable[l].name}
              </button>
            ))}
            <button type="button" className="ml-auto font-medium text-brand hover:underline" onClick={() => set({ labelIds: [...new Set([...draft.labelIds, ...aiPicks])] })}>
              Apply all
            </button>
          </div>
        )}
        <div className="flex items-center gap-3 border-t px-4 py-2.5 max-sm:pb-[max(10px,env(safe-area-inset-bottom))]">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <FileText className="size-3.5" /> {team?.name}
          </span>
          <span className="flex-1" />
          {hasContent && (
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={discard} title="Throw this draft away">
              <Trash2 /> <span className="max-sm:hidden">Discard</span>
            </Button>
          )}
          <label className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <Switch checked={more} onCheckedChange={setMore} size="sm" /> Create more
          </label>
          <Button size="sm" onClick={submit}>
            Create issue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
